import { useReactiveVar } from '@apollo/client';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { cleanPayload } from 'src/helpers/common';
import viewStateVar from 'src/store/viewState';
import type { DensityType } from 'src/types/store.types';
import { ActionsCell, DynamicFieldCell, NameCell, SelectCell, ThumbnailCell, TypeBadgeCell } from './cells';
import type { ListViewColumnMeta, ListViewRow } from './listView.types';
import { COLUMN_MIN_WIDTHS } from './listView.types';

interface ViewFieldNode {
  fieldLabel: string;
  sequence: number;
  field?: {
    id?: string;
    slug: string;
    name?: string;
    scope?: string[];
  };
}

// Cell components consume row data via ListViewRowContext — column defs just wire them up
export function useListViewColumns(density: DensityType): ColumnDef<ListViewRow>[] {
  const viewState = useReactiveVar(viewStateVar);
  const ViewMediaCardListView = viewState.viewModels?.library?.ViewMediaCardListView;

  return useMemo(() => {
    // Fixed leading columns
    const selectColumn: ColumnDef<ListViewRow> = {
      id: 'select',
      header: '',
      cell: () => <SelectCell />,
      size: COLUMN_MIN_WIDTHS.select,
      minSize: COLUMN_MIN_WIDTHS.select,
      maxSize: COLUMN_MIN_WIDTHS.select,
      enableSorting: false,
      enableResizing: false,
    };

    const typeColumn: ColumnDef<ListViewRow> = {
      id: 'type',
      header: 'Type',
      cell: () => <TypeBadgeCell />,
      size: COLUMN_MIN_WIDTHS.type,
      minSize: COLUMN_MIN_WIDTHS.type,
      maxSize: COLUMN_MIN_WIDTHS.type,
      enableSorting: false,
      enableResizing: false,
    };

    const thumbSize =
      density === 'spacious' ? COLUMN_MIN_WIDTHS.thumbnailSpacious : COLUMN_MIN_WIDTHS.thumbnailComfortable;
    const thumbnailColumn: ColumnDef<ListViewRow> = {
      id: 'thumbnail',
      header: '',
      cell: () => <ThumbnailCell />,
      size: thumbSize,
      minSize: thumbSize,
      enableSorting: false,
      enableResizing: false,
    };

    // Dynamic columns from the view model
    const dynamicColumns: ColumnDef<ListViewRow>[] = [];

    if (ViewMediaCardListView?.node) {
      const viewFields = cleanPayload<ViewFieldNode>(ViewMediaCardListView.node);
      const sorted = [...viewFields].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

      for (const [index, viewField] of sorted.entries()) {
        const columnId = `field_${viewField.sequence ?? dynamicColumns.length}_${viewField.field?.slug ?? 'unknown'}`;

        const meta: ListViewColumnMeta = {
          fieldLabel: viewField.fieldLabel,
          sequence: viewField.sequence,
          slug: viewField.field?.slug,
          name: viewField.field?.name,
          scope: viewField.field?.scope,
          isNameColumn: index === 0,
        };

        dynamicColumns.push({
          id: columnId,
          header: viewField.fieldLabel,
          cell:
            index === 0
              ? (info) => <NameCell slug={info.column.columnDef.meta?.slug} />
              : (info) => <DynamicFieldCell slug={info.column.columnDef.meta?.slug} />,
          meta,
          minSize: COLUMN_MIN_WIDTHS.dynamic,
          enableSorting: false,
          enableResizing: true,
        });
      }
    }

    // Fixed trailing column
    const actionsColumn: ColumnDef<ListViewRow> = {
      id: 'actions',
      header: '',
      cell: () => <ActionsCell />,
      size: COLUMN_MIN_WIDTHS.actions,
      minSize: COLUMN_MIN_WIDTHS.actions,
      maxSize: COLUMN_MIN_WIDTHS.actions,
      enableSorting: false,
      enableResizing: false,
    };

    const leading: ColumnDef<ListViewRow>[] =
      density === 'compact' ? [selectColumn, typeColumn] : [selectColumn, typeColumn, thumbnailColumn];

    return [...leading, ...dynamicColumns, actionsColumn];
  }, [ViewMediaCardListView, density]);
}
